<script setup lang="ts">
import {
  ArchiveBoxIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  PaperAirplaneIcon,
  WrenchScrewdriverIcon,
  XCircleIcon,
} from "@heroicons/vue/24/outline"

type TimelineIcon = "create" | "send" | "receive" | "factory" | "done" | "cancel" | "clock"
type TimelineState = "done" | "pending" | "warning" | "danger"

type TimelineStep = {
  id: string
  title: string
  description: string
  icon: TimelineIcon
  state: TimelineState
}

const props = defineProps<{
  steps: TimelineStep[]
}>()

const iconByKey = {
  create: DocumentTextIcon,
  send: PaperAirplaneIcon,
  receive: ArchiveBoxIcon,
  factory: WrenchScrewdriverIcon,
  done: CheckCircleIcon,
  cancel: XCircleIcon,
  clock: ClockIcon,
}

const stateClasses: Record<TimelineState, { point: string; title: string; description: string }> = {
  done: {
    point: "border-emerald-300 bg-emerald-100 text-emerald-700",
    title: "text-slate-800",
    description: "text-slate-500",
  },
  pending: {
    point: "border-slate-300 bg-white text-slate-400",
    title: "text-slate-400",
    description: "text-slate-400",
  },
  warning: {
    point: "border-amber-300 bg-amber-100 text-amber-700",
    title: "text-amber-700",
    description: "text-slate-500",
  },
  danger: {
    point: "border-rose-300 bg-rose-100 text-rose-700",
    title: "text-rose-700",
    description: "text-slate-500",
  },
}
</script>

<template>
  <ol class="relative ml-1 space-y-4 pl-6 before:absolute before:left-1.5 before:top-2 before:h-[calc(100%-1rem)] before:w-px before:bg-slate-200">
    <li v-for="step in props.steps" :key="step.id" class="relative">
      <div class="absolute -left-6 top-0.5 flex size-4 items-center justify-center rounded-full border" :class="stateClasses[step.state].point">
        <component :is="iconByKey[step.icon]" class="size-2.5" />
      </div>
      <p class="text-sm font-medium" :class="stateClasses[step.state].title">{{ step.title }}</p>
      <p class="text-xs" :class="stateClasses[step.state].description">{{ step.description }}</p>
    </li>
  </ol>
</template>

